import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BaseSelectViewModel,
  BuildingRoomViewModel,
  CreateArchObjUtilHistoryRequest,
  FrozenZoneViewModel,
  LocationClient,
  MassEditRoomRequest,
  UpdateArchObjUtilHistoryRequest,
  UpdateRoomRequest,
} from '@core/api';
import {
  catchError,
  forkJoin,
  Observable,
  Subject,
  switchMap,
  of,
  shareReplay,
  merge,
  map,
  finalize,
} from 'rxjs';

interface EditModalRoomState {
  isLoading: Signal<boolean>;
  modalEditRoomWrapper: Signal<{
    calculationTypes: BaseSelectViewModel[];
    billingAreas: BaseSelectViewModel[];
    centralInfos: BaseSelectViewModel[];
    officeAreaTypes: BaseSelectViewModel[];
    changeReasons: BaseSelectViewModel[];
    buildingMaintenanceRoomInformation: BuildingRoomViewModel;
  }>;
}

interface EditRoomComponentState {
  isLoading: Signal<boolean>;
  EditRoomWrapper: Signal<{
    frozenZoneStatus: FrozenZoneViewModel;
    wmvClusters: BaseSelectViewModel[];
    wmvClusterInfos: BaseSelectViewModel[];
    wmvProducts: BaseSelectViewModel[];
    calculationTypes: BaseSelectViewModel[];
    billingAreas: BaseSelectViewModel[];
    centralInfos: BaseSelectViewModel[];
    officeAreaTypes: BaseSelectViewModel[];
    changeReasons: BaseSelectViewModel[];
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class EditRoomService {
  readonly locationClient = inject(LocationClient);

  readonly dropdownOptionsTiFlagAndIsRoutineCleaning = [
    { label: 'Hinzufügen', value: true },
    { label: 'Entfernen', value: false },
  ];
  //Modal edit rooms component
  readonly load$ = new Subject<string>();
  readonly retrieveData$ = this.load$.pipe(
    switchMap(id =>
      forkJoin({
        calculationTypes: this.getCalculationTypes().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        billingAreas: this.getBillingAreas(id).pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        centralInfos: this.getCentralInfos().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        officeAreaTypes: this.getOfficeAreaTypes().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        changeReasons: this.getChangeReasons().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        buildingMaintenanceRoomInformation:
          this.getBuildingMaintenanceRoomInformation(id).pipe(
            catchError(() => of({} as BuildingRoomViewModel))
          ),
      })
    ),
    shareReplay(1)
  );
  readonly isLoading$ = merge(
    this.load$.pipe(map(() => true)),
    this.retrieveData$.pipe(map(() => false))
  );

  readonly isLoadingToSignal = toSignal(this.isLoading$, {
    initialValue: true,
  });

  isLoadingSubmit = signal(false);

  isLoading = computed(() => {
    const isSubmiting = this.isLoadingSubmit();
    const isLoading = this.isLoadingToSignal();

    return isSubmiting || isLoading;
  });

  readonly retrieveDataSignal = toSignal(this.retrieveData$, {
    initialValue: {
      calculationTypes: [] as BaseSelectViewModel[],
      billingAreas: [] as BaseSelectViewModel[],
      centralInfos: [] as BaseSelectViewModel[],
      officeAreaTypes: [] as BaseSelectViewModel[],
      changeReasons: [] as BaseSelectViewModel[],
      buildingMaintenanceRoomInformation: {} as BuildingRoomViewModel,
    },
  });

  state: EditModalRoomState = {
    modalEditRoomWrapper: this.retrieveDataSignal,
    isLoading: this.isLoading,
  };

  loadDataModalEditRooms(spaceId: string): void {
    this.load$.next(spaceId);
  }

  //Edit room component
  readonly loadEditRoom$ = new Subject<string>();
  readonly retrieveDataEditRoom$ = this.loadEditRoom$.pipe(
    switchMap(id =>
      forkJoin({
        frozenZoneStatus: this.checkFrozenZone().pipe(
          catchError(() => of({ startDate: '', endDate: '' }))
        ),
        changeReasons: this.getChangeReasons().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        wmvClusters: this.getWmvClusters().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        wmvClusterInfos: this.getWmVClusterInfos().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        wmvProducts: this.getWmvProducts().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        calculationTypes: this.getCalculationTypes().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        billingAreas: this.getBillingAreas(id).pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        centralInfos: this.getCentralInfos().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
        officeAreaTypes: this.getOfficeAreaTypes().pipe(
          catchError(() => of(Array<BaseSelectViewModel>()))
        ),
      })
    ),
    shareReplay(1)
  );

  readonly isLoadingEditRoom$ = merge(
    this.loadEditRoom$.pipe(map(() => true)),
    this.retrieveDataEditRoom$.pipe(map(() => false))
  );

  readonly isLoadingEditRoomToSignal = toSignal(this.isLoadingEditRoom$, {
    initialValue: true,
  });

  readonly retrieveDataEditRoomSignal = toSignal(this.retrieveDataEditRoom$, {
    initialValue: {
      frozenZoneStatus: { startDate: '', endDate: '' },
      wmvClusters: [] as BaseSelectViewModel[],
      wmvClusterInfos: [] as BaseSelectViewModel[],
      wmvProducts: [] as BaseSelectViewModel[],
      calculationTypes: [] as BaseSelectViewModel[],
      billingAreas: [] as BaseSelectViewModel[],
      centralInfos: [] as BaseSelectViewModel[],
      officeAreaTypes: [] as BaseSelectViewModel[],
      changeReasons: [] as BaseSelectViewModel[],
    },
  });

  loadDataEditRooms(spaceId: string): void {
    this.loadEditRoom$.next(spaceId);
  }

  stateEditRoom: EditRoomComponentState = {
    EditRoomWrapper: this.retrieveDataEditRoomSignal,
    isLoading: this.isLoadingEditRoomToSignal,
  };

  getBuildingMaintenanceRoomInformation(
    spaceId: string | undefined
  ): Observable<BuildingRoomViewModel> {
    return this.locationClient.getBuildingMaintenanceRoomInformation(spaceId);
  }

  addArchObjUtilHistory(
    data: CreateArchObjUtilHistoryRequest
  ): Observable<void> {
    return this.locationClient.addArchObjUtilHistory(data);
  }

  updateArchObjUtilHistory(
    data: UpdateArchObjUtilHistoryRequest
  ): Observable<void> {
    return this.locationClient.updateArchObjUtilHistory(data);
  }

  deleteArchObjUtilHistory(archObjUtilHistoryId: string): Observable<void> {
    return this.locationClient.deleteArchObjUtilHistory(archObjUtilHistoryId);
  }

  updateRoom(request: UpdateRoomRequest) {
    return this.locationClient.updateRoom(request);
  }

  getChangeReasons(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getChangeReasons();
  }

  getWmvClusters(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getWmvClusters();
  }

  getWmVClusterInfos(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getWmvClusterInfos();
  }

  getWmvProducts(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getWmvProducts();
  }

  getCalculationTypes(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getCalculationTypes();
  }

  getBillingAreas(
    archObjId: string | undefined
  ): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getBillingAreas(archObjId);
  }

  getCentralInfos(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getCentralInfos();
  }

  getOfficeAreaTypes(): Observable<BaseSelectViewModel[]> {
    return this.locationClient.getOfficeAreaTypes();
  }

  checkFrozenZone(): Observable<FrozenZoneViewModel> {
    return this.locationClient.checkFrozenZone();
  }

  massEditRooms(request: MassEditRoomRequest): Observable<void> {
    this.isLoadingSubmit.set(true);
    return this.locationClient
      .massEditRooms(request)
      .pipe(finalize(() => this.isLoadingSubmit.set(false)));
  }
}
